import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { LogEntry } from "features/CenterContent/VehicleDetails/shared/LogEntry";
import React, { useMemo, useState } from "react";
import { DismissibleInfoMessage } from "shared/components/feedback/InfoMessage/DismissibleInfoMessage";
import { Box, Button, Text, Textarea, tokens } from "shared/components/Spor";
import {
  AlternativeTransportLog,
  AlternativeTransportLogReq,
} from "shared/types/alternativeTransport";
import { compareDateStringAsc } from "shared/utils/dateTimeUtils";
import styled from "styled-components";

const inputMsg = "Skriv loggmelding";
const OperationalLogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: visible;
  width: 100%;
  padding: 0;
`;

const LogList = styled.ul`
  overflow-y: visible;
  min-height: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  row-gap: 0px;
`;

type OperationalLogProps = {
  vehicleId: string;
  logs: AlternativeTransportLog[];
  distributionStopPlaces?: string;
  textareaMinHeight?: string;
};

const getDisplayMessage = (message: string | null, tags: string[]) =>
  [message, ...tags].filter(Boolean).join(tags.length > 0 ? ". " : " ");

const OperationalLog = ({
  vehicleId,
  logs,
  distributionStopPlaces,
  textareaMinHeight = "152px",
}: OperationalLogProps) => {
  const [logMessage, setLogMessage] = useState("");

  const sortedLogs = useMemo(
    () =>
      logs.toSorted((a, b) => compareDateStringAsc(a.timestamp, b.timestamp)),
    [logs],
  );

  const operationalLogMutation = useMutation({
    mutationKey: ["postAlternativeTransportLogs", vehicleId],
    mutationFn: (data: AlternativeTransportLogReq) =>
      mutationFnPOST<AlternativeTransportLog, AlternativeTransportLogReq>(
        `${getBackendUrl()}/alternativeTransportLogs/${vehicleId}`,
        data,
      ),
  });

  function onSubmit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();

    operationalLogMutation.mutate({
      message: logMessage,
      distributionStopPlaces: distributionStopPlaces ?? "",
      alternativeTransportId: vehicleId,
    });

    setLogMessage("");
  }

  const noLogs = logs.length === 0;

  return (
    <OperationalLogWrapper>
      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",

          display: "flex",
          flexDirection: "column",
          gap: tokens.default.size.spacing[2],
          marginBottom: tokens.default.size.spacing[2],
        }}
      >
        <Textarea
          key={vehicleId}
          size="sm"
          fontSize={["mobile.xs", "desktop.xs"]}
          fontWeight="normal"
          pt={2}
          label={inputMsg}
          value={logMessage}
          onChange={(e) => {
            setLogMessage(e.target.value);
          }}
          minH={textareaMinHeight ?? "10rem"}
          resize="none"
        />
        <DismissibleInfoMessage
          semantics="alarm"
          summary="Kunne ikke sende loggmelding. Prøv på nytt eller kontakt IT."
          isDismissed={operationalLogMutation.status !== "error"}
          onDismiss={operationalLogMutation.reset}
        />
        <Button
          size="sm"
          variant="secondary"
          type="submit"
          loading={operationalLogMutation.status === "pending"}
          disabled={logMessage.length === 0}
          ml="auto"
        >
          Send melding
        </Button>
      </form>

      <LogList>
        {sortedLogs.map(
          (
            { id, timestamp, message, sourceSystem, locationName, tags },
            index,
          ) => (
            <LogEntry
              key={id}
              timestamp={timestamp}
              message={getDisplayMessage(message, tags)}
              source={locationName}
              fallbackSource={sourceSystem.name}
              hideDashedLine={index === sortedLogs.length - 1}
            />
          ),
        )}
      </LogList>

      {noLogs && (
        <Box padding="2">
          <Text fontWeight={400} color="text.subtle">
            Ingen meldinger i loggen
          </Text>
        </Box>
      )}
    </OperationalLogWrapper>
  );
};

export default OperationalLog;
